import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Home.css";
import HomeCarousel from "./HomeCarousel";
import IntroStory from "./IntroStory";

const FEATURES = [
  {
    title: "Vision planning",
    desc: "Write your future across every category you care about, from 1 to 5 years out. Each category becomes a record with its own timeline, build log, and live countdown.",
    visual: (
      <svg viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="vp-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fafafa"/>
            <stop offset="100%" stopColor="#f0f0f0"/>
          </linearGradient>
          <filter id="vp-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08"/>
          </filter>
        </defs>
        <rect width="360" height="220" fill="url(#vp-bg)"/>
        {/* Header bar */}
        <rect x="20" y="18" width="320" height="22" rx="6" fill="#fff" stroke="#e8e8e8"/>
        <circle cx="32" cy="29" r="3" fill="#f17501"/>
        <rect x="42" y="26" width="80" height="6" rx="3" fill="#333"/>
        <rect x="290" y="24" width="40" height="10" rx="5" fill="#fff8f0" stroke="#f17501" strokeWidth="0.8"/>
        <text x="310" y="31.5" textAnchor="middle" fontSize="6" fill="#f17501" fontWeight="700" letterSpacing="0.5">+ NEW</text>

        {/* Card 1 — Career (active/highlighted) */}
        <g filter="url(#vp-shadow)">
          <rect x="20" y="54" width="100" height="68" rx="8" fill="#fff" stroke="#f17501" strokeWidth="1.2"/>
          <circle cx="32" cy="68" r="4" fill="#f17501"/>
          <rect x="42" y="65" width="40" height="5" rx="2.5" fill="#0a0a0a"/>
          <rect x="42" y="74" width="28" height="3" rx="1.5" fill="#bbb"/>
          <rect x="30" y="86" width="80" height="3" rx="1.5" fill="#f0f0f0"/>
          <rect x="30" y="86" width="56" height="3" rx="1.5" fill="#f17501"/>
          <text x="30" y="103" fontSize="6.5" fill="#999" letterSpacing="0.3">247 days left</text>
          <text x="106" y="103" fontSize="6.5" fill="#f17501" fontWeight="700" textAnchor="end">68%</text>
          <rect x="30" y="108" width="20" height="8" rx="2" fill="#f7f7f7"/>
          <text x="40" y="114" fontSize="5" fill="#888" textAnchor="middle">12 logs</text>
        </g>

        {/* Card 2 — Health */}
        <g filter="url(#vp-shadow)">
          <rect x="130" y="54" width="100" height="68" rx="8" fill="#fff" stroke="#e8e8e8"/>
          <circle cx="142" cy="68" r="4" fill="#16a34a"/>
          <rect x="152" y="65" width="40" height="5" rx="2.5" fill="#0a0a0a"/>
          <rect x="152" y="74" width="28" height="3" rx="1.5" fill="#bbb"/>
          <rect x="140" y="86" width="80" height="3" rx="1.5" fill="#f0f0f0"/>
          <rect x="140" y="86" width="32" height="3" rx="1.5" fill="#16a34a"/>
          <text x="140" y="103" fontSize="6.5" fill="#999" letterSpacing="0.3">412 days left</text>
          <text x="216" y="103" fontSize="6.5" fill="#16a34a" fontWeight="700" textAnchor="end">40%</text>
        </g>

        {/* Card 3 — Mind */}
        <g filter="url(#vp-shadow)">
          <rect x="240" y="54" width="100" height="68" rx="8" fill="#fff" stroke="#e8e8e8"/>
          <circle cx="252" cy="68" r="4" fill="#7c3aed"/>
          <rect x="262" y="65" width="40" height="5" rx="2.5" fill="#0a0a0a"/>
          <rect x="262" y="74" width="28" height="3" rx="1.5" fill="#bbb"/>
          <rect x="250" y="86" width="80" height="3" rx="1.5" fill="#f0f0f0"/>
          <rect x="250" y="86" width="20" height="3" rx="1.5" fill="#7c3aed"/>
          <text x="250" y="103" fontSize="6.5" fill="#999" letterSpacing="0.3">580 days left</text>
          <text x="326" y="103" fontSize="6.5" fill="#7c3aed" fontWeight="700" textAnchor="end">25%</text>
        </g>

        {/* Bottom timeline strip */}
        <g filter="url(#vp-shadow)">
          <rect x="20" y="138" width="320" height="60" rx="8" fill="#fff" stroke="#e8e8e8"/>
          <text x="32" y="153" fontSize="7" fill="#999" letterSpacing="1" fontWeight="600">5-YEAR TIMELINE</text>

          {/* Year markers */}
          <line x1="32" y1="180" x2="328" y2="180" stroke="#eee" strokeWidth="1"/>
          {[0, 1, 2, 3, 4, 5].map((y) => (
            <g key={y}>
              <circle cx={32 + y * 59.2} cy="180" r="2" fill="#ddd"/>
              <text x={32 + y * 59.2} y="193" fontSize="5.5" fill="#bbb" textAnchor="middle">Y{y}</text>
            </g>
          ))}
          {/* Active node positions */}
          <circle cx="91.2" cy="180" r="3.5" fill="#f17501"/>
          <circle cx="150.4" cy="180" r="3.5" fill="#16a34a"/>
          <circle cx="209.6" cy="180" r="3.5" fill="#7c3aed"/>
          <line x1="91.2" y1="172" x2="91.2" y2="178" stroke="#f17501" strokeWidth="1"/>
          <text x="91.2" y="170" fontSize="5" fill="#f17501" textAnchor="middle" fontWeight="700">CAREER</text>
        </g>
      </svg>
    ),
  },
  {
    title: "Cosmos view",
    desc: "All your records together in one spatial view. See every reality you're building, where each one is, and how your whole vision is taking shape.",
    visual: (
      <svg viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="cv-bg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#0a0e1a"/>
            <stop offset="100%" stopColor="#000"/>
          </radialGradient>
          <radialGradient id="cv-orange" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f17501" stopOpacity="1"/>
            <stop offset="60%" stopColor="#f17501" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#f17501" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="cv-green" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#16a34a" stopOpacity="1"/>
            <stop offset="60%" stopColor="#16a34a" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="cv-purple" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="1"/>
            <stop offset="60%" stopColor="#7c3aed" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0"/>
          </radialGradient>
        </defs>
        <rect width="360" height="220" fill="url(#cv-bg)"/>

        {/* Stars */}
        {[
          [22,18,0.6],[55,42,0.8],[88,15,0.5],[140,28,0.7],[195,12,0.6],[245,38,0.8],[290,20,0.5],[330,45,0.7],
          [12,80,0.5],[40,110,0.7],[78,95,0.6],[112,140,0.8],[165,175,0.6],[210,195,0.5],[260,165,0.7],[315,180,0.6],
          [338,110,0.8],[185,108,0.4],[68,178,0.5],[225,80,0.6],[295,135,0.7],[145,55,0.5],[5,150,0.6],[350,75,0.7],
          [250,200,0.5],[35,195,0.6],[100,205,0.4],[270,5,0.5],[180,200,0.6]
        ].map(([x,y,r], i) => (
          <circle key={i} cx={x} cy={y} r={r} fill="#fff" opacity={0.4 + Math.random() * 0.5}/>
        ))}

        {/* Orange planet (top-left) with orbit ring */}
        <ellipse cx="100" cy="60" rx="22" ry="6" stroke="#f17501" strokeWidth="0.6" opacity="0.35" transform="rotate(-15 100 60)"/>
        <circle cx="100" cy="60" r="18" fill="url(#cv-orange)"/>
        <circle cx="100" cy="60" r="8" fill="#f17501"/>
        <text x="100" y="38" fontSize="6" fill="#f17501" textAnchor="middle" letterSpacing="0.5" opacity="0.9">CAREER</text>

        {/* Green planet (bottom) */}
        <ellipse cx="200" cy="170" rx="20" ry="5" stroke="#16a34a" strokeWidth="0.6" opacity="0.35" transform="rotate(20 200 170)"/>
        <circle cx="200" cy="170" r="16" fill="url(#cv-green)"/>
        <circle cx="200" cy="170" r="7" fill="#16a34a"/>

        {/* Purple planet (right) */}
        <ellipse cx="295" cy="125" rx="18" ry="5" stroke="#7c3aed" strokeWidth="0.6" opacity="0.35" transform="rotate(-25 295 125)"/>
        <circle cx="295" cy="125" r="14" fill="url(#cv-purple)"/>
        <circle cx="295" cy="125" r="6" fill="#7c3aed"/>

        {/* Center "you" — small white anchor */}
        <circle cx="180" cy="110" r="3" fill="#fff"/>
        <text x="186" y="113" fontSize="6" fill="#aaa">Day 12 · You are here</text>

        {/* Curved trajectory lines */}
        <path d="M 180 110 Q 140 80, 100 60" stroke="#f17501" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round"/>
        <path d="M 180 110 Q 195 145, 200 170" stroke="#16a34a" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round"/>
        <path d="M 180 110 Q 240 115, 295 125" stroke="#7c3aed" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round"/>

        {/* Date label bottom-right */}
        <text x="340" y="210" fontSize="6" fill="#666" textAnchor="end" letterSpacing="0.4">07 May 2026</text>
      </svg>
    ),
  },
  {
    title: "Live countdown",
    desc: "Your dashboard shows exactly how many days remain. A commitment with a visible timer is a commitment you actually keep.",
    visual: (
      <svg viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lc-bg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#fff8f2"/>
            <stop offset="100%" stopColor="#fef0e3"/>
          </linearGradient>
        </defs>
        <rect width="360" height="220" fill="url(#lc-bg)"/>

        {/* Main timer card */}
        <rect x="40" y="30" width="280" height="120" rx="14" fill="#fff" stroke="#f0e0cc" strokeWidth="1.5"/>

        {/* Status row */}
        <circle cx="56" cy="46" r="3" fill="#16a34a">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite"/>
        </circle>
        <text x="64" y="49" fontSize="8" fill="#16a34a" fontWeight="700" letterSpacing="0.5">LIVE</text>
        <text x="304" y="49" fontSize="7" fill="#999" textAnchor="end" letterSpacing="0.4">CAREER · YEAR 1</text>

        {/* Big number — 247 */}
        <text x="180" y="105" textAnchor="middle" fontFamily="Georgia,serif" fontSize="58" fontWeight="700" fill="#0a0a0a" letterSpacing="-1">247</text>
        <text x="180" y="125" textAnchor="middle" fontSize="9" fill="#999" letterSpacing="3" fontWeight="600">DAYS REMAINING</text>

        {/* Time breakdown row */}
        <line x1="60" y1="138" x2="300" y2="138" stroke="#f0e0cc" strokeWidth="0.8"/>

        {/* Progress bar */}
        <rect x="40" y="170" width="280" height="6" rx="3" fill="#fff" stroke="#f0e0cc" strokeWidth="0.8"/>
        <rect x="40" y="170" width="170" height="6" rx="3" fill="#f17501"/>
        <text x="40" y="194" fontSize="7" fill="#999" letterSpacing="0.4">Started 14 Jan 2026</text>
        <text x="320" y="194" fontSize="7" fill="#999" textAnchor="end" letterSpacing="0.4">Release 09 Jan 2027</text>

        {/* Sub-stats */}
        <g>
          <rect x="60" y="142" width="60" height="22" rx="4" fill="#fff8f0"/>
          <text x="90" y="152" fontSize="6" fill="#999" textAnchor="middle" letterSpacing="0.3">HOURS</text>
          <text x="90" y="161" fontSize="9" fill="#f17501" textAnchor="middle" fontWeight="700">5,928</text>

          <rect x="150" y="142" width="60" height="22" rx="4" fill="#fff8f0"/>
          <text x="180" y="152" fontSize="6" fill="#999" textAnchor="middle" letterSpacing="0.3">UPDATES</text>
          <text x="180" y="161" fontSize="9" fill="#f17501" textAnchor="middle" fontWeight="700">14</text>

          <rect x="240" y="142" width="60" height="22" rx="4" fill="#fff8f0"/>
          <text x="270" y="152" fontSize="6" fill="#999" textAnchor="middle" letterSpacing="0.3">PROGRESS</text>
          <text x="270" y="161" fontSize="9" fill="#f17501" textAnchor="middle" fontWeight="700">61%</text>
        </g>
      </svg>
    ),
  },
  {
    title: "AI assistance",
    desc: "Stuck on what to do next? Dmyor Assist reads your record and your updates, then gives you specific next steps for where you actually are.",
    visual: (
      <svg viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="ai-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fafafa"/>
            <stop offset="100%" stopColor="#f0f0f0"/>
          </linearGradient>
          <filter id="ai-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08"/>
          </filter>
        </defs>
        <rect width="360" height="220" fill="url(#ai-bg)"/>

        {/* Chat header pill */}
        <g filter="url(#ai-shadow)">
          <rect x="20" y="14" width="320" height="22" rx="11" fill="#fff" stroke="#e8e8e8"/>
          <circle cx="32" cy="25" r="4" fill="#f17501"/>
          <text x="42" y="28" fontSize="7" fill="#0a0a0a" fontWeight="700">Dmyor Assist</text>
          <rect x="290" y="20" width="42" height="10" rx="5" fill="#fff8f0" stroke="#f17501" strokeWidth="0.6"/>
          <text x="311" y="27" fontSize="6" fill="#f17501" textAnchor="middle" fontWeight="700" letterSpacing="0.4">STRATEGIST</text>
        </g>

        {/* User bubble (right aligned) */}
        <g filter="url(#ai-shadow)">
          <path d="M 130 50 Q 130 46, 134 46 L 326 46 Q 330 46, 330 50 L 330 76 Q 330 80, 326 80 L 154 80 L 148 86 L 150 80 L 134 80 Q 130 80, 130 76 Z" fill="#f7f7f7" stroke="#e8e8e8" strokeWidth="0.8"/>
          <text x="140" y="60" fontSize="7" fill="#333">Two weeks since I posted. Feel like</text>
          <text x="140" y="71" fontSize="7" fill="#333">I'm falling behind.</text>
        </g>

        {/* AI bubble (left aligned) */}
        <g filter="url(#ai-shadow)">
          <path d="M 30 96 Q 30 92, 34 92 L 246 92 Q 250 92, 250 96 L 250 196 Q 250 200, 246 200 L 50 200 L 44 206 L 46 200 L 34 200 Q 30 200, 30 196 Z" fill="#fff" stroke="#f17501" strokeWidth="1"/>
          <rect x="40" y="100" width="48" height="11" rx="5.5" fill="#fff8f0"/>
          <text x="64" y="108" fontSize="6" fill="#f17501" textAnchor="middle" fontWeight="700" letterSpacing="0.4">STRATEGIST</text>

          <text x="40" y="123" fontSize="7" fill="#0a0a0a" fontWeight="600">14 updates logged. Not behind</text>
          <text x="40" y="133" fontSize="7" fill="#0a0a0a" fontWeight="600">— between sprints.</text>

          {/* Numbered steps */}
          <g>
            <circle cx="46" cy="148" r="6" fill="#f17501"/>
            <text x="46" y="151" fontSize="7" fill="#fff" textAnchor="middle" fontWeight="700">1</text>
            <text x="58" y="151" fontSize="6.5" fill="#555">Ship one piece this week.</text>
          </g>
          <g>
            <circle cx="46" cy="166" r="6" fill="#f17501"/>
            <text x="46" y="169" fontSize="7" fill="#fff" textAnchor="middle" fontWeight="700">2</text>
            <text x="58" y="169" fontSize="6.5" fill="#555">Review last batch's wins.</text>
          </g>
          <g>
            <circle cx="46" cy="184" r="6" fill="#f17501"/>
            <text x="46" y="187" fontSize="7" fill="#fff" textAnchor="middle" fontWeight="700">3</text>
            <text x="58" y="187" fontSize="6.5" fill="#555">Block weekend to batch ahead.</text>
          </g>
        </g>

        {/* Mode pills (bottom right) */}
        <g>
          <rect x="262" y="100" width="36" height="11" rx="5.5" fill="#fff" stroke="#e8e8e8"/>
          <text x="280" y="108" fontSize="5.5" fill="#999" textAnchor="middle">Discuss</text>
          <rect x="262" y="115" width="36" height="11" rx="5.5" fill="#fff" stroke="#e8e8e8"/>
          <text x="280" y="123" fontSize="5.5" fill="#999" textAnchor="middle">Mirror</text>
          <rect x="262" y="130" width="36" height="11" rx="5.5" fill="#fff" stroke="#e8e8e8"/>
          <text x="280" y="138" fontSize="5.5" fill="#999" textAnchor="middle">Challenge</text>
          <rect x="262" y="145" width="36" height="11" rx="5.5" fill="#fff8f0" stroke="#f17501"/>
          <text x="280" y="153" fontSize="5.5" fill="#f17501" textAnchor="middle" fontWeight="700">Strategist</text>
        </g>
      </svg>
    ),
  },
  {
    title: "Visibility on your terms",
    desc: "Everything is private by default. You choose what goes to the Explore feed, when, and how much. Your name only appears when you release.",
    visual: (
      <svg viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="vis-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fafafa"/>
            <stop offset="100%" stopColor="#f0f0f0"/>
          </linearGradient>
          <filter id="vis-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08"/>
          </filter>
        </defs>
        <rect width="360" height="220" fill="url(#vis-bg)"/>

        {/* Card 1: PRIVATE (left, dimmer) */}
        <g filter="url(#vis-shadow)" opacity="0.85">
          <rect x="20" y="40" width="150" height="140" rx="10" fill="#fff" stroke="#e8e8e8"/>

          {/* Lock icon */}
          <g>
            <rect x="80" y="60" width="20" height="14" rx="2" fill="#666"/>
            <path d="M 84 60 Q 84 52, 90 52 Q 96 52, 96 60" stroke="#666" strokeWidth="1.5" fill="none"/>
            <circle cx="90" cy="67" r="1.5" fill="#fff"/>
          </g>

          <text x="95" y="92" fontSize="9" fill="#0a0a0a" textAnchor="middle" fontWeight="700" letterSpacing="0.4">ANONYMOUS</text>
          <text x="95" y="104" fontSize="7" fill="#999" textAnchor="middle">Default</text>

          {/* Hidden author */}
          <line x1="40" y1="120" x2="150" y2="120" stroke="#f0f0f0"/>
          <rect x="40" y="130" width="14" height="14" rx="7" fill="#f0f0f0"/>
          <text x="61" y="140" fontSize="6" fill="#bbb">———————</text>
          <text x="61" y="150" fontSize="5.5" fill="#bbb">writing in private</text>

          <rect x="40" y="160" width="80" height="3" rx="1.5" fill="#f0f0f0"/>
          <rect x="40" y="167" width="50" height="3" rx="1.5" fill="#f0f0f0"/>
        </g>

        {/* Arrow connector */}
        <g>
          <line x1="178" y1="110" x2="195" y2="110" stroke="#f17501" strokeWidth="1.5" strokeDasharray="3 2"/>
          <path d="M 192 106 L 198 110 L 192 114" stroke="#f17501" strokeWidth="1.5" fill="none"/>
          <text x="187" y="100" fontSize="5.5" fill="#f17501" textAnchor="middle" fontWeight="700" letterSpacing="0.4">RELEASE</text>
        </g>

        {/* Card 2: RELEASED (right, vivid) */}
        <g filter="url(#vis-shadow)">
          <rect x="205" y="40" width="150" height="140" rx="10" fill="#fff" stroke="#f17501" strokeWidth="1.2"/>

          {/* Eye/badge icon */}
          <g>
            <circle cx="280" cy="65" r="11" fill="#f17501"/>
            <path d="M 273 65 Q 280 58, 287 65 Q 280 72, 273 65 Z" fill="#fff"/>
            <circle cx="280" cy="65" r="2.5" fill="#f17501"/>
          </g>

          <text x="280" y="92" fontSize="9" fill="#0a0a0a" textAnchor="middle" fontWeight="700" letterSpacing="0.4">RELEASED</text>
          <text x="280" y="104" fontSize="7" fill="#16a34a" textAnchor="middle" fontWeight="600">Live · 2026</text>

          {/* Now showing author */}
          <line x1="225" y1="120" x2="335" y2="120" stroke="#f0f0f0"/>
          <circle cx="232" cy="137" r="7" fill="#f17501"/>
          <text x="232" y="140" fontSize="7" fill="#fff" textAnchor="middle" fontWeight="700">S</text>
          <text x="246" y="135" fontSize="7" fill="#0a0a0a" fontWeight="600">Sanyam P.</text>
          <text x="246" y="144" fontSize="5.5" fill="#999">released after 247 days</text>

          <rect x="225" y="155" width="110" height="3" rx="1.5" fill="#f17501" opacity="0.4"/>
          <rect x="225" y="162" width="80" height="3" rx="1.5" fill="#f17501" opacity="0.4"/>
        </g>

        {/* Bottom label */}
        <text x="180" y="205" textAnchor="middle" fontSize="7" fill="#666" letterSpacing="0.4">Your name appears only when the reality is real</text>
      </svg>
    ),
  },
  {
    title: "Journey analysis",
    desc: "A full read of where you are versus where you said you'd be. What's on track, what's falling behind, and what needs your attention now.",
    visual: (
      <svg viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="ja-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fafafa"/>
            <stop offset="100%" stopColor="#f4f4f4"/>
          </linearGradient>
          <linearGradient id="ja-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f17501" stopOpacity="0.28"/>
            <stop offset="100%" stopColor="#f17501" stopOpacity="0"/>
          </linearGradient>
        </defs>

        {/* Full bleed background */}
        <rect width="360" height="220" fill="url(#ja-bg)"/>

        {/* Header row — edge to edge, top padding only */}
        <text x="20" y="22" fontSize="9" fill="#0a0a0a" fontWeight="700">Journey Analysis</text>
        <text x="20" y="33" fontSize="6.5" fill="#999" letterSpacing="0.4">Career · Day 89 of 365</text>

        {/* Status pills — right aligned */}
        <rect x="218" y="14" width="46" height="13" rx="6.5" fill="#f0fdf4" stroke="#16a34a" strokeWidth="0.6"/>
        <circle cx="226" cy="20.5" r="2" fill="#16a34a"/>
        <text x="232" y="22.5" fontSize="5.5" fill="#16a34a" fontWeight="700" letterSpacing="0.3">ON TRACK</text>

        <rect x="270" y="14" width="70" height="13" rx="6.5" fill="#fff8f0" stroke="#f17501" strokeWidth="0.6"/>
        <text x="305" y="22.5" fontSize="5.5" fill="#f17501" textAnchor="middle" fontWeight="700" letterSpacing="0.3">+8% AHEAD</text>

        {/* Chart — fills from x=36 to x=354, y=44 to y=188 */}
        <line x1="36" y1="44" x2="36" y2="188" stroke="#e4e4e4" strokeWidth="0.8"/>
        <line x1="36" y1="188" x2="354" y2="188" stroke="#e4e4e4" strokeWidth="0.8"/>

        {/* Y-axis labels */}
        <text x="30" y="48" fontSize="5.5" fill="#bbb" textAnchor="end">100%</text>
        <text x="30" y="116" fontSize="5.5" fill="#bbb" textAnchor="end">50%</text>
        <text x="30" y="190" fontSize="5.5" fill="#bbb" textAnchor="end">0%</text>

        {/* Grid lines */}
        <line x1="36" y1="48" x2="354" y2="48" stroke="#ebebeb" strokeWidth="0.6"/>
        <line x1="36" y1="116" x2="354" y2="116" stroke="#ebebeb" strokeWidth="0.6"/>
        <line x1="36" y1="152" x2="354" y2="152" stroke="#ebebeb" strokeWidth="0.6"/>

        {/* Planned trajectory (dashed gray) */}
        <polyline points="36,178 90,160 145,138 200,112 255,88 310,68 354,54" stroke="#ccc" strokeWidth="1.2" strokeDasharray="3 3" fill="none"/>

        {/* Actual area fill */}
        <path d="M 36 178 L 90 165 L 145 140 L 200 108 L 255 80 L 310 60 L 354 46 L 354 188 L 36 188 Z" fill="url(#ja-area)"/>

        {/* Actual trajectory (solid orange) */}
        <polyline points="36,178 90,165 145,140 200,108 255,80 310,60 354,46" stroke="#f17501" strokeWidth="2.2" fill="none" strokeLinejoin="round" strokeLinecap="round"/>

        {/* Data points */}
        {[[36,178],[90,165],[145,140],[200,108],[255,80],[310,60]].map(([x,y], i) => (
          <circle key={i} cx={x} cy={y} r="2.5" fill="#fff" stroke="#f17501" strokeWidth="1.5"/>
        ))}
        <circle cx="354" cy="46" r="4.5" fill="#f17501"/>
        <circle cx="354" cy="46" r="8" fill="#f17501" opacity="0.18"/>

        {/* X-axis labels */}
        <text x="36" y="200" fontSize="5.5" fill="#bbb" textAnchor="middle">Jan</text>
        <text x="90" y="200" fontSize="5.5" fill="#bbb" textAnchor="middle">Feb</text>
        <text x="145" y="200" fontSize="5.5" fill="#bbb" textAnchor="middle">Mar</text>
        <text x="200" y="200" fontSize="5.5" fill="#bbb" textAnchor="middle">Apr</text>
        <text x="255" y="200" fontSize="5.5" fill="#bbb" textAnchor="middle">May</text>
        <text x="310" y="200" fontSize="5.5" fill="#bbb" textAnchor="middle">Jun</text>
        <text x="354" y="200" fontSize="5.5" fill="#f17501" textAnchor="middle" fontWeight="700">Now</text>

        {/* Legend */}
        <line x1="20" y1="213" x2="32" y2="213" stroke="#f17501" strokeWidth="2"/>
        <text x="36" y="215" fontSize="6" fill="#666">Actual</text>
        <line x1="72" y1="213" x2="84" y2="213" stroke="#ccc" strokeWidth="1.2" strokeDasharray="2 2"/>
        <text x="88" y="215" fontSize="6" fill="#666">Planned</text>
      </svg>
    ),
  },
];

const Home = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [featureIdx, setFeatureIdx] = useState(0);

  const [showIntro, setShowIntro] = useState(false);

  const dismissIntro = (nextPath) => {
    setShowIntro(false);
    if (nextPath) navigate(nextPath);
  };
  const fcTouchX = useRef(null);

  const prevFeature = () => setFeatureIdx(i => (i - 1 + FEATURES.length) % FEATURES.length);
  const nextFeature = () => setFeatureIdx(i => (i + 1) % FEATURES.length);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.15 }
    );

    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home">
      {showIntro && <IntroStory onClose={dismissIntro} />}
      <Helmet>
        <title>dmyor | Your personal intent archive</title>
        <meta name="description" content="Your personal intent archive. Write what you're going to build before you build it. Document every step. Keep a permanent record of how far you got." />
        <link rel="canonical" href="https://www.dmyor.com/" />
        <meta property="og:title" content="dmyor | Your personal intent archive" />
        <meta property="og:description" content="Your personal intent archive. Write what you're going to build before you build it. Document every step. Keep a permanent record of how far you got." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.dmyor.com/" />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="dmyor | Your personal intent archive" />
        <meta name="twitter:description" content="Your personal intent archive. Write what you're going to build before you build it. Document every step. Keep a permanent record of how far you got." />
        <meta name="twitter:image" content="https://dmyor.com/images/og-image.png" />
      </Helmet>

      {/* ── STORY TRIGGER ── */}
      <div className="story-trigger-wrap">
        <button className="story-trigger-btn" onClick={() => setShowIntro(true)}>
          <span className="story-trigger-dot" />
          Understand dmyor in 5 slides
          <span className="story-trigger-arrow">→</span>
        </button>
      </div>

      {/* ── HERO ── */}
      <section className="hero" ref={heroRef}>
        <div className="hero-inner">
          <div className="hero-label reveal">Reality Construction Platform</div>

          <h1 className="hero-title reveal">
            Write the reality<br />
            that doesn't<br />
            <span className="accent">exist yet.</span>
          </h1>

          <p className="hero-sub reveal">
            Everyone has something they're going to do. Few write it down. Fewer still keep a record of how they got there.
          </p>

          <div className="hero-actions reveal">
            <Link to="/dashboard/planning/new" className="btn-primary">
              Write your reality
            </Link>
            <Link to="/about" className="btn-ghost">
              Our story
            </Link>
          </div>

          <div className="hero-footnote reveal">
            Anonymous until you build it. 
          </div>
          <div className="hero-ai-badge reveal">
            <span className="hero-ai-dot" />
            AI-powered planning inside every record
          </div>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="blueprint-grid"></div>
          <div className="floating-node node-1">
            <span className="node-status active">Active</span>
            <span className="node-text">Buy my first place before 30.</span>
            <span className="node-days">847 days remaining</span>
          </div>
          <div className="floating-node node-2">
            <span className="node-status active">Active</span>
            <span className="node-text">Lose 20kg in one year. No excuses.</span>
            <span className="node-days">312 days remaining</span>
          </div>
          <div className="floating-node node-3">
            <span className="node-status active">Active</span>
            <span className="node-text">Scale my instagram to 100K</span>
            <span className="node-days">214 days remaining</span>
          </div>
        </div>
      </section>

      {/* ── INTENT ARCHIVE ── */}
      <section className="ia-section">
        <div className="ia-inner">

          <div className="ia-header reveal">
            <div className="section-eyebrow">Intent Archive</div>
            <h2 className="ia-heading">
              The world's <span className="ia-heading-accent">intent archive.</span>
            </h2>
            <p className="ia-body">
              A record of intentions, dreams, and journeys — written by people who decided to start. Kept for the future to read.
            </p>
          </div>

          <div className="ia-log reveal">
            <div className="ia-log-head">
              <span>Year</span>
              <span>Author</span>
              <span>Vision</span>
              <span>Status</span>
            </div>
            <div className="ia-log-row">
              <span className="ia-log-year">2028</span>
              <span className="ia-log-author">S.P.</span>
              <p className="ia-log-text">"I'm starting my own business this year. By year 5, it's paying for everything I want to do."</p>
              <span className="ia-card-status released">Released</span>
            </div>
            <div className="ia-log-row">
              <span className="ia-log-year">2027</span>
              <span className="ia-log-author">M.R.</span>
              <p className="ia-log-text">"I'm building my Instagram page on AI tools. 100k followers in 3 years, brand deals by year 2."</p>
              <span className="ia-card-status released">Released</span>
            </div>
            <div className="ia-log-row">
              <span className="ia-log-year">2026</span>
              <span className="ia-log-author">D.K.</span>
              <p className="ia-log-text">"I'm building enough investments to retire by 40. Five years of serious saving and compounding starts now."</p>
              <span className="ia-card-status released">Released</span>
            </div>
            <div className="ia-log-row ia-log-row-you">
              <span className="ia-log-year ia-log-year-you">2026</span>
              <span className="ia-log-author">You</span>
              <p className="ia-log-text ia-log-text-pending"><span className="ia-card-cursor">|</span></p>
              <span className="ia-card-status awaiting">Awaiting</span>
            </div>
          </div>

          <div className="ia-cta-row reveal">
            <Link to="/dashboard/planning/new" className="ia-cta">
              <span>Write your chapter</span>
            </Link>
          </div>

        </div>
      </section>

      {/* ── WE HELP YOU FIGURE IT OUT ── */}
      <section className="fig-section">
        <div className="fig-inner">

          <div className="fig-header reveal">
            <div className="section-eyebrow">Figure it out</div>
            <h2 className="fig-heading">
              We saw what you dream of doing. <span className="fig-heading-accent">Now we help you make it real.</span>
            </h2>
            <p className="fig-body">
              You don't need to know the path. Dmyor Assist walks it with you.
            </p>
          </div>

          <div className="fig-example reveal">
            <span className="fig-example-eyebrow">Following one vision</span>
            <p className="fig-example-vision">"I want to start the business I've been thinking about for years and finally work for myself."</p>
          </div>

          <div className="fig-flow">

            {/* Step 1 */}
            <div className="fig-step reveal">
              <div className="fig-step-grid">
                <div className="fig-step-text">
                  <div className="fig-step-meta">
                    <span className="fig-step-num">01</span>
                    <span className="fig-step-tag">Records</span>
                  </div>
                  <h3 className="fig-step-title">Dmyor Assist asks the right questions, and helps you shape your records.</h3>
                  <p className="fig-step-desc">
                    Records are what you're doing to make your vision real. Dmyor Assist asks the right questions and helps you shape them.
                  </p>
                </div>
                <div className="fig-step-card">
                  <div className="fig-card-head">
                    <span className="fig-card-eyebrow">Dmyor Assist</span>
                    <span className="fig-card-badge">Intake</span>
                  </div>
                  <div className="fig-chat fig-chat-ai">
                    <p>What's the business actually about? And do you have a customer in mind?</p>
                  </div>
                  <div className="fig-chat fig-chat-you">
                    <p>A coaching service for new managers. People who got promoted but feel lost.</p>
                  </div>
                  <div className="fig-chat fig-chat-ai">
                    <p>Clear. Here are three records to start with:</p>
                  </div>
                  <div className="fig-records">
                    <div className="fig-record">
                      <span className="fig-record-dot" style={{ background: "#16a34a" }} />
                      <span>Talking to 20 new managers to learn what they're really struggling with.</span>
                    </div>
                    <div className="fig-record">
                      <span className="fig-record-dot" style={{ background: "#f17501" }} />
                      <span>Building a small audience by sharing what I learn online.</span>
                    </div>
                    <div className="fig-record">
                      <span className="fig-record-dot" style={{ background: "#7c3aed" }} />
                      <span>Shaping my first paid offer and getting it in front of real people.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="fig-step reveal">
              <div className="fig-step-grid fig-step-grid-reverse">
                <div className="fig-step-card">
                  <div className="fig-card-head">
                    <span className="fig-card-eyebrow">Dmyor Assist</span>
                    <span className="fig-card-badge fig-card-badge-violet">This week</span>
                  </div>
                  <div className="fig-todo-list">
                    <div className="fig-todo-item">
                      <span className="fig-todo-check" />
                      <span className="fig-todo-text">Reach out to 5 new managers in your network for a 20-min call</span>
                      <span className="fig-todo-pill fig-todo-pill-blue">Focus</span>
                    </div>
                    <div className="fig-todo-item">
                      <span className="fig-todo-check" />
                      <span className="fig-todo-text">Write one post about the most common struggle you've heard so far</span>
                      <span className="fig-todo-pill fig-todo-pill-green">Quick</span>
                    </div>
                    <div className="fig-todo-item">
                      <span className="fig-todo-check" />
                      <span className="fig-todo-text">Draft the rough outline of your first paid offer by Friday</span>
                      <span className="fig-todo-pill fig-todo-pill-orange">Push</span>
                    </div>
                  </div>
                </div>
                <div className="fig-step-text">
                  <div className="fig-step-meta">
                    <span className="fig-step-num">02</span>
                    <span className="fig-step-tag">Todos</span>
                  </div>
                  <h3 className="fig-step-title">Then it turns those records into a clear week of work.</h3>
                  <p className="fig-step-desc">
                    Dmyor Assist turns your records into specific weekly todos. No more guessing what to do.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="fig-step reveal">
              <div className="fig-step-grid">
                <div className="fig-step-text">
                  <div className="fig-step-meta">
                    <span className="fig-step-num">03</span>
                    <span className="fig-step-tag">Journey</span>
                  </div>
                  <h3 className="fig-step-title">Every step becomes a documented journey.</h3>
                  <p className="fig-step-desc">
                    Every update, every win, every lesson is saved. A year later, it's all there.
                  </p>
                </div>
                <div className="fig-step-card">
                  <div className="fig-card-head">
                    <span className="fig-card-eyebrow">Your Journey</span>
                    <span className="fig-card-badge fig-card-badge-orange">Live</span>
                  </div>
                  <div className="fig-timeline">
                    <div className="fig-timeline-item">
                      <span className="fig-timeline-dot" />
                      <div className="fig-timeline-content">
                        <span className="fig-timeline-date">Feb 12</span>
                        <p>Did the first 5 calls. Same pattern in every conversation: nobody taught them how to lead.</p>
                      </div>
                    </div>
                    <div className="fig-timeline-item">
                      <span className="fig-timeline-dot" />
                      <div className="fig-timeline-content">
                        <span className="fig-timeline-date">Mar 04</span>
                        <p>First post hit 400 likes. Three people DM'd asking if I work with managers 1-on-1.</p>
                      </div>
                    </div>
                    <div className="fig-timeline-item">
                      <span className="fig-timeline-dot fig-timeline-dot-active" />
                      <div className="fig-timeline-content">
                        <span className="fig-timeline-date">Apr 19</span>
                        <p>First paying client signed. Six week program. This is actually a thing now.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <p className="fig-universal reveal">This works for anything you're trying to build, become, or finish.</p>

          <div className="fig-cta-row reveal">
            <Link to="/dashboard/planning/new" className="fig-cta">
              <span>Start with Dmyor Assist</span>
            </Link>
          </div>

        </div>
      </section>

      {/* ── VISION LIFECYCLE ── */}
      <section className="vlc-section">
        <div className="vlc-inner">

          <div className="vlc-header reveal">
            <h2 className="vlc-heading">
              Vision <span className="vlc-heading-accent">Lifecycle.</span>
            </h2>
          </div>

          <div className="vlc-circles reveal">

            <div className="vlc-node">
              <div className="vlc-circle">
                <span className="vlc-circle-num">01</span>
              </div>
              <h3 className="vlc-node-title">You write the vision.</h3>
              <p className="vlc-node-desc">Start your journey.</p>
            </div>

            <span className="vlc-arrow" aria-hidden="true">→</span>

            <div className="vlc-node">
              <div className="vlc-circle">
                <span className="vlc-circle-num">02</span>
              </div>
              <h3 className="vlc-node-title">You make it real.</h3>
              <p className="vlc-node-desc">Document every struggle, every small win.</p>
            </div>

            <span className="vlc-arrow" aria-hidden="true">→</span>

            <div className="vlc-node">
              <div className="vlc-circle vlc-circle-final">
                <span className="vlc-circle-num">03</span>
              </div>
              <h3 className="vlc-node-title">You release it.</h3>
              <p className="vlc-node-desc">It now has your name on it, permanently. Everyone can read your journey now!</p>
            </div>

          </div>

        </div>
      </section>

      {/* ── HONEST WITH YOURSELF ── */}
      <section className="dr-section">
        <div className="dr-inner">

          <div className="dr-left reveal">
            <div className="dr-note">I want to build something.</div>
            <div className="dr-note">In 1 year my life should be different.</div>
            <div className="dr-note">I know what I want. I just don't know where to start.</div>
            <div className="dr-note">I'm going to make this thing happen.</div>
          </div>

          <div className="dr-right reveal">
            <h2 className="dr-heading">
              Don't forget why<br />you started.
            </h2>
            <p className="dr-sell-body">
              Ever had these thoughts? Write down what you want and document the process as you go. Most people underestimate how much just putting it into words can clear things up.
            </p>
            <p className="dr-sell-body">
              Most people forget why they started. The frustration, the spark, the moment something clicked. Keep your roots — your future self will need to read them back.
            </p>
            <Link to="/dashboard/planning/new" className="dr-cta">Start your record</Link>
          </div>

        </div>
      </section>

      {/* ── PROJECT-5 STRIP ── */}
      <section className="p5-strip">
        <div className="p5-strip-inner reveal">
          <div className="p5-strip-left">
            <span className="p5-strip-title">PROJECT-5</span>
            <span className="p5-strip-sub">A 5-year commitment to designing the life you want. Write it. Build it. Release it when it's real.</span>
          </div>
          <Link to="/project-5" className="p5-strip-link" aria-label="Learn about Project 5">
            ?
          </Link>
        </div>
      </section>

      <HomeCarousel />

      {/* ── ANONYMOUS UNTIL RELEASED ── */}
      <section className="anon-section">
        <div className="anon-inner">
          <div className="anon-header reveal">
            <div className="section-eyebrow">Privacy</div>
            <h2 className="anon-heading">
              <span className="anon-heading-accent">Anonymous</span> by default.
            </h2>
            <p className="anon-body">
              Anyone can read what you wrote. No one can see who wrote it. When you've made it real, release the record to attach your name.
            </p>
          </div>

          <div className="anon-cards reveal">
            <div className="anon-card">
              <span className="anon-card-status">Anonymous</span>
              <p className="anon-card-text">"I will finally start the business I've been thinking about for 3 years."</p>
              <div className="anon-card-author">
                <span className="anon-card-author-label">Author</span>
                <span className="anon-redacted">REDACTED</span>
              </div>
              <p className="anon-card-note">The default. Your record is public. You aren't.</p>
            </div>

            <div className="anon-card">
              <span className="anon-card-status anon-status-private">Hidden</span>
              <p className="anon-card-text">"I'm going to get my finances in order before I turn 30."</p>
              <div className="anon-card-author">
                <span className="anon-card-author-label">Author</span>
                <span className="anon-private-name">Only you</span>
              </div>
              <p className="anon-card-note">Mark it private. It leaves the archive entirely.</p>
            </div>

            <div className="anon-card">
              <span className="anon-card-status anon-status-released">Released</span>
              <p className="anon-card-text">"I stopped waiting and shipped. Here's how it happened."</p>
              <div className="anon-card-author">
                <span className="anon-card-author-label">Author</span>
                <span className="anon-author-real">Aman</span>
              </div>
              <p className="anon-card-note">When you've made it real, release it. Your name appears.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY DOCUMENT ── */}
      <section className="wd-section">
        <div className="wd-inner">
          <div className="wd-header reveal">
            <div className="section-eyebrow">Why document</div>
            <h2>Because the record matters.</h2>
          </div>

          <div className="wd-grid">
            <div className="wd-item reveal">
              <span className="wd-num">01</span>
              <div>
                <h4>Your future self will read this.</h4>
                <p>Before the outcome, before the proof — there was a version of you who just decided. That moment deserves to be kept.</p>
              </div>
            </div>

            <div className="wd-item reveal">
              <span className="wd-num">02</span>
              <div>
                <h4>The world deserves to know these thoughts existed.</h4>
                <p>Someone will read your story one day and choose to start. Your intention matters beyond you.</p>
              </div>
            </div>

            <div className="wd-item reveal">
              <span className="wd-num">03</span>
              <div>
                <h4>Most intentions never leave the mind.</h4>
                <p>Writing yours down is the first act of making it real. Everything else follows from that.</p>
              </div>
            </div>

            <div className="wd-item reveal">
              <span className="wd-num">04</span>
              <div>
                <h4>You cannot see your own growth without a record.</h4>
                <p>Distance only becomes visible when you can look back at where you started.</p>
              </div>
            </div>

            <div className="wd-item reveal">
              <span className="wd-num">05</span>
              <div>
                <h4>Memory rewrites the past. Writing doesn't.</h4>
                <p>Your updates are timestamped and permanent — the real story of how it happened, not the edited version you'll remember later.</p>
              </div>
            </div>

            <div className="wd-item reveal">
              <span className="wd-num">06</span>
              <div>
                <h4>Written intentions are harder to abandon.</h4>
                <p>Commitment that lives only in your head is easy to walk away from. A written record holds you to what you said you would do.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RECORD LIFECYCLE ── */}
      <section className="rl-section">
        <div className="rl-inner">
          <div className="rl-header reveal">
            <div className="section-eyebrow">Lifecycle</div>
            <h2 className="rl-heading">From <span className="rl-heading-accent">first word</span> to <span className="rl-heading-accent">final release</span>.</h2>
            <p className="rl-body">A record isn't a one-time post. It lives, grows, and ends on your terms.</p>
          </div>

          <div className="rl-timeline">
            <div className="rl-line" aria-hidden="true"></div>

            <div className="rl-stage rl-stage-left reveal">
              <div className="rl-node">
                <span className="rl-node-num">01</span>
              </div>
              <div className="rl-content">
                <span className="rl-stage-tag">Create</span>
                <h3 className="rl-stage-title">Set the record. Set the duration.</h3>
                <p className="rl-stage-desc">Write the one thing you're going to do. Pick how long you're giving yourself: 1 year, 3 years, 5 years. The clock starts ticking the moment you commit.</p>
              </div>
            </div>

            <div className="rl-stage rl-stage-right reveal">
              <div className="rl-node">
                <span className="rl-node-num">02</span>
              </div>
              <div className="rl-content">
                <span className="rl-stage-tag">Build</span>
                <h3 className="rl-stage-title">Update as you go. AI Assist when you're stuck.</h3>
                <p className="rl-stage-desc">Log progress, breakthroughs, setbacks. Talk to AI Assist to think through next steps, plan side quests, or pressure-test your strategy. The record grows with you.</p>
              </div>
            </div>

            <div className="rl-stage rl-stage-left reveal">
              <div className="rl-node">
                <span className="rl-node-num">03</span>
              </div>
              <div className="rl-content">
                <span className="rl-stage-tag">Release</span>
                <h3 className="rl-stage-title">When it's real, release it.</h3>
                <p className="rl-stage-desc">The record becomes public under your name, with the entire build log attached. Permanent proof you said you'd do it, then did it.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DMYOR AI ── */}
      <section className="dma-section">
        <div className="dma-inner">

          <div className="dma-left reveal">
            <div className="section-eyebrow">Dmyor Assist</div>
            <h2>Need someone to think this through with?</h2>
            <p className="dma-sub">
              Your AI reads everything you've written — your record, your updates,
              your timeline. It meets you where you actually are.
            </p>

            <div className="dma-modes">
              {[
                { color: "#16a34a", label: "Discuss",    desc: "Just talk through where you are." },
                { color: "#7c3aed", label: "Challenge",  desc: "Get pushed on what you're avoiding." },
                { color: "#0891b2", label: "Mirror",     desc: "Hear your own thinking reflected back." },
                { color: "#be185d", label: "Positive",   desc: "Perspective and grounded encouragement." },
                { color: "#f17501", label: "Strategist", desc: "Specific next steps based on your reality." },
              ].map((m) => (
                <div className="dma-mode" key={m.label}>
                  <span className="dma-mode-dot" style={{ background: m.color }} />
                  <div className="dma-mode-text">
                    <span className="dma-mode-label">{m.label}</span>
                    <span className="dma-mode-desc">{m.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="dma-right reveal">
            <div className="dma-mockup">
              <div className="dma-mockup-header">
                <span className="dma-mockup-title">Dmyor Assist</span>
                <span className="dma-mockup-badge" style={{ background: "#f17501" }}>Strategist</span>
              </div>

              <div className="dma-mockup-node">
                <span className="dma-mockup-node-label">Record</span>
                <span className="dma-mockup-node-text">100K followers on Instagram, brand deals, and a product of my own.</span>
              </div>

              <div className="dma-mockup-chat">
                <div className="dma-bubble dma-bubble-user">
                  I haven't posted anything in two weeks. Feel like I'm falling behind.
                </div>

                <div className="dma-bubble dma-bubble-ai">
                  <span className="dma-bubble-mode" style={{ color: "#f17501" }}>Strategist</span>
                  <p>You've logged 14 updates since you started — you're not falling behind, you're between sprints.</p>
                  <div className="dma-bubble-steps">
                    <div className="dma-bubble-step"><span className="dma-step-num">1</span><span>Post one piece of content this week. Anything. Ship it.</span></div>
                    <div className="dma-bubble-step"><span className="dma-step-num">2</span><span>Review what performed best in your last batch.</span></div>
                    <div className="dma-bubble-step"><span className="dma-step-num">3</span><span>Block 2 hours this weekend to batch-create ahead.</span></div>
                  </div>
                </div>
              </div>

              <div className="dma-mockup-switcher">
                <span className="dma-switcher-label">Switch mode</span>
                <div className="dma-switcher-pills">
                  {[
                    { label: "Discuss",    color: "#16a34a" },
                    { label: "Challenge",  color: "#7c3aed" },
                    { label: "Mirror",     color: "#0891b2" },
                    { label: "Positive",   color: "#be185d" },
                    { label: "Strategist", color: "#f17501" },
                  ].map((m) => (
                    <span
                      key={m.label}
                      className={`dma-pill ${m.label === "Strategist" ? "dma-pill-active" : ""}`}
                      style={m.label === "Strategist" ? { borderColor: m.color, color: m.color, background: `${m.color}12` } : {}}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── VISION CREATION — commented out, restore if needed ──
      <section className="vision-section">
        <div className="vision-inner">
          <div className="vision-header reveal">
            <div className="section-eyebrow">The main thing</div>
            <h2>
              A vision is where
              <span className="accent">everything starts.</span>
            </h2>
            <p className="vision-header-sub">
              Pick a timeline from 1 to 5 years. Write what your life looks
              like by then, across every dimension that matters to you.
              Career, health, money, relationships, creative work. However
              many you want. However deep you want to go.
            </p>
          </div>

          <div className="vision-cards">
            <div className="vision-card reveal">
              <div className="vision-card-num">01</div>
              <div className="vision-card-body">
                <h3>Pick your dimensions</h3>
                <p>Choose the areas of your life you are building. Add as many as you want, or create your own.</p>
              </div>
            </div>
            <div className="vision-card reveal">
              <div className="vision-card-num">02</div>
              <div className="vision-card-body">
                <h3>Write the reality</h3>
                <p>For each dimension, write where you will be. Not where you hope to be. Where you will be. Write it as if it already exists.</p>
              </div>
            </div>
            <div className="vision-card reveal">
              <div className="vision-card-num">03</div>
              <div className="vision-card-body">
                <h3>Set your window</h3>
                <p>Anywhere from 1 to 5 years. The timer starts the moment you create it. Every day that passes is visible on your dashboard.</p>
              </div>
            </div>
            <div className="vision-card reveal">
              <div className="vision-card-num">04</div>
              <div className="vision-card-body">
                <h3>Build and document</h3>
                <p>Every decision, every update, every hard stretch. Your journey lives inside your vision, timestamped and permanently yours.</p>
              </div>
            </div>
          </div>

          <div className="vision-cta reveal">
            <Link to="/login" className="btn-primary">Create your vision</Link>
            <span className="vision-cta-note">Anonymous by default. Free to start.</span>
          </div>
        </div>
      </section>
      ── end commented section ── */}

      {/* ── PROOF STRIP ── */}
      <section className="transition-section">
        <div className="transition-inner reveal">
          <div className="transition-card transition-then">
            <span className="transition-label">Written</span>
            <p className="transition-text">"I am starting a YouTube channel. 100k subscribers in 2 years. I have zero videos right now."</p>
            <span className="transition-meta">Committed. Anonymous. Day one.</span>
          </div>

          <div className="transition-arrow" aria-hidden="true">
            <div className="arrow-line"></div>
            <span className="arrow-gap">2 years later</span>
            <div className="arrow-line"></div>
          </div>

          <div className="transition-card transition-now">
            <span className="transition-label released">Released</span>
            <p className="transition-text">"112k subscribers. 87 videos. The channel is real. I wrote this before I even bought a mic."</p>
            <span className="transition-meta">V.S., released after 23 months</span>
          </div>
        </div>
      </section>


      {/* ── ANONYMOUS ── */}
      <section className="anon">
        <div className="anon-inner reveal">
          <h2>
            Anonymous until<br />
            you earn the name.
          </h2>
          <div className="anon-body">
            <p>
              Everything written on dmyor is anonymous by default. No
              audience. No performance. No one watching you fail or pivot or
              go quiet for three months.
            </p>
            <p>
              The only moment your name appears is when you release. When the
              reality you wrote has become real. That is the only credential
              that matters here.
            </p>
            <Link to="/s" className="btn-ghost-orange">
              See what people are building →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="cta-final">
        <div className="cta-inner reveal">
          <p className="cta-pre">The only question is:</p>
          <h2 className="cta-title">
            Are you ready to design your<br />
            <span className="accent">reality?</span>
          </h2>
          <p className="cta-body">
            Write the reality that doesn't exist yet. Set the deadline.
            Start building. Release it when it's real.
          </p>
          <Link to="/login" className="btn-primary btn-large">
            Write your first reality
          </Link>
          <p className="cta-note">Anonymous. Yours.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
